// src/components/mentions/ZoneMentions.tsx — SCE Issues
// Une zone de saisie où « @ » propose des personnes (02/11) : commentaires et
// descriptions des problèmes. On tape « @thi », le menu montre les personnes
// de Pompey (annuaire) ; ↑ ↓ puis Entrée ou Tab, ou un clic, écrit
// « @Thibaut Hacquin » dans le texte. Échap ferme le menu (pas la fenêtre).
//
// Dès « @ » seul : les personnes de la fiche (`proposes` : déclarant,
// coordinateurs, auteurs de l'activité). La personne choisie est retenue pour
// l'onglet (api/mentions) : c'est à l'enregistrement que le texte dit qui
// prévenir.
//
// Un <textarea> ordinaire pour le reste : le parent garde ses styles, son
// collage de fichiers, ses raccourcis (onKeyDown n'est appelé que si le menu
// ne prend pas la touche).

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
  type TextareaHTMLAttributes,
} from 'react'
import { createPortal } from 'react-dom'
import { rechercherPompey } from '@/api/annuaire.api'
import { retenirMention } from '@/api/mentions.api'
import { insererMention, requeteMention, type PersonneMentionnee, type RequeteMention } from '@/utils/mentions'
import { PastillePersonne } from '@/components/personnes/PastillePersonne'

export type ZoneMentionsProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> & {
  value: string
  onValeur: (texte: string) => void
  /** Proposés dès « @ » : les personnes de la fiche. */
  proposes?: readonly PersonneMentionnee[]
  /** Pas de menu (fournisseur : l'annuaire ne lui est pas ouvert). */
  sansMentions?: boolean
}

interface Option extends PersonneMentionnee {
  detail: string
}

const sansAccents = (t: string): string =>
  t
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()

/** « thi », « hac », « thibaut h » : le début du nom ou d'un de ses mots. */
function correspond(nom: string, q: string): boolean {
  const n = sansAccents(nom)
  const r = sansAccents(q.trim())
  return !r || n.startsWith(r) || n.split(/[\s-]+/).some((m) => m.startsWith(r))
}

export const ZoneMentions = forwardRef<HTMLTextAreaElement, ZoneMentionsProps>(function ZoneMentions(
  { value, onValeur, proposes = [], sansMentions = false, onKeyDown, onBlur, onFocus, onClick, onKeyUp, ...reste },
  ref,
) {
  const zone = useRef<HTMLTextAreaElement>(null)
  useImperativeHandle(ref, () => zone.current as HTMLTextAreaElement, [])

  const [requete, setRequete] = useState<RequeteMention | null>(null)
  // Le « @ » d'une mention qu'on vient d'écrire ou de fermer : plus de menu pour lui.
  const ignore = useRef<number | null>(null)
  const [actif, setActif] = useState(0)
  const [resultats, setResultats] = useState<Option[] | null>(null)
  const [cherche, setCherche] = useState(false)
  const derniere = useRef(0)

  const relire = useCallback(
    (el: HTMLTextAreaElement): void => {
      if (sansMentions) return
      const r = requeteMention(el.value, el.selectionStart ?? el.value.length)
      if (ignore.current !== null && r?.debut !== ignore.current) ignore.current = null
      setRequete(r && r.debut !== ignore.current ? r : null)
    },
    [sansMentions],
  )

  const q = requete?.texte.trim() ?? ''
  const enSaisie = !!requete

  // L'annuaire, dès deux lettres (Pompey seulement, comme ChoixPersonne).
  useEffect(() => {
    if (!enSaisie || q.length < 2) {
      setResultats(null)
      setCherche(false)
      return
    }
    setCherche(true)
    const n = ++derniere.current
    const minuterie = window.setTimeout(() => {
      rechercherPompey(q, 10)
        .then((r) => {
          if (n !== derniere.current) return
          setResultats(r.map((p) => ({ nom: p.nom, upn: p.upn, detail: [p.service, p.poste].filter(Boolean).join(' — ') })))
        })
        .catch(() => n === derniere.current && setResultats([]))
        .finally(() => n === derniere.current && setCherche(false))
    }, 200)
    return () => window.clearTimeout(minuterie)
  }, [enSaisie, q])

  const vus = new Set<string>()
  const options: Option[] = []
  for (const o of [...proposes.filter((p) => correspond(p.nom, q)).map((p) => ({ ...p, detail: '' })), ...(resultats ?? [])]) {
    const cle = o.upn.toLowerCase()
    if (!o.nom.trim() || !cle || vus.has(cle)) continue
    vus.add(cle)
    options.push(o)
  }
  const visibles = options.slice(0, 8)

  useEffect(() => setActif(0), [q, visibles.length])

  const fermer = (): void => {
    if (requete) ignore.current = requete.debut
    setRequete(null)
  }

  // iPhone (02/11) : toucher un nom fait perdre le focus à la zone AVANT le
  // clic ; fermer tout de suite effacerait le menu sous le doigt. On ferme un
  // instant après, sauf si le choix est arrivé entre-temps.
  const fermeture = useRef<number | null>(null)
  const annulerFermeture = (): void => {
    if (fermeture.current !== null) window.clearTimeout(fermeture.current)
    fermeture.current = null
  }
  useEffect(() => annulerFermeture, [])

  const choisir = (o: Option): void => {
    annulerFermeture()
    const el = zone.current
    if (!el || !requete) return
    retenirMention({ nom: o.nom, upn: o.upn })
    // La fin de ce qui est tapé, pas le curseur : il a pu bouger avec le focus.
    const r = insererMention(value, requete.debut, requete.debut + 1 + requete.texte.length, o.nom)
    ignore.current = requete.debut
    setRequete(null)
    onValeur(r.texte)
    window.requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(r.curseur, r.curseur)
    })
  }

  const ouvert = !!requete
  const surTouche = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (ouvert) {
      if (e.key === 'ArrowDown' && visibles.length) {
        e.preventDefault()
        setActif((i) => Math.min(i + 1, visibles.length - 1))
        return
      }
      if (e.key === 'ArrowUp' && visibles.length) {
        e.preventDefault()
        setActif((i) => Math.max(i - 1, 0))
        return
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && !e.ctrlKey && !e.metaKey && visibles[actif]) {
        e.preventDefault()
        choisir(visibles[actif])
        return
      }
      if (e.key === 'Escape') {
        // Ferme le menu, pas la fenêtre dessous.
        e.preventDefault()
        e.stopPropagation()
        fermer()
        return
      }
    }
    onKeyDown?.(e)
  }

  return (
    <>
      <textarea
        {...reste}
        ref={zone}
        value={value}
        onChange={(e) => {
          onValeur(e.target.value)
          relire(e.target)
        }}
        onKeyDown={surTouche}
        onKeyUp={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') relire(e.currentTarget)
          onKeyUp?.(e)
        }}
        onClick={(e) => {
          relire(e.currentTarget)
          onClick?.(e)
        }}
        onBlur={(e) => {
          annulerFermeture()
          fermeture.current = window.setTimeout(() => {
            fermeture.current = null
            setRequete(null)
          }, 300)
          onBlur?.(e)
        }}
        onFocus={(e) => {
          annulerFermeture()
          onFocus?.(e)
        }}
        aria-autocomplete={sansMentions ? undefined : 'list'}
        aria-expanded={sansMentions ? undefined : ouvert}
      />
      {ouvert && zone.current && (
        <MenuMentions
          ancre={zone.current}
          options={visibles}
          actif={actif}
          message={
            visibles.length
              ? null
              : q.length < 2
                ? 'Tapez le nom de la personne à mentionner'
                : cherche || resultats === null
                  ? 'Recherche…'
                  : 'Personne de ce nom à Pompey'
          }
          onChoisir={choisir}
        />
      )}
    </>
  )
})

/**
 * Sous la zone, ou au-dessus quand la place manque (zone de commentaire en bas
 * d'écran, clavier du téléphone).
 *
 * iPhone (02/11 : le menu ne se voyait pas) : clavier ouvert, Safari ne
 * réduit que la partie VISIBLE de l'écran (visualViewport), décalée dans la
 * page ; un élément fixe, lui, reste calé sur l'écran entier — le menu tombait
 * derrière le clavier. On ne suppose donc rien : le menu est posé, on mesure
 * où il est arrivé, et la zone visible se lit dans les mêmes coordonnées que
 * la zone de saisie.
 */
function placerMenu(menu: HTMLElement, ancre: HTMLElement): void {
  menu.style.top = '0px'
  menu.style.transform = 'none'
  // Où arrive un « top: 0 » dans les coordonnées des getBoundingClientRect.
  const origine = menu.getBoundingClientRect().top
  const vue = window.visualViewport
  const hautVisible = origine + (vue?.offsetTop ?? 0)
  const basVisible = hautVisible + (vue?.height ?? window.innerHeight)
  const largeurVisible = vue?.width ?? window.innerWidth

  const r = ancre.getBoundingClientRect()
  const largeur = Math.min(340, Math.max(260, r.width), largeurVisible - 16)
  const gauche = Math.max(8, Math.min(r.left, largeurVisible - largeur - 8))
  const dessous = basVisible - r.bottom
  const dessus = r.top - hautVisible
  menu.style.left = `${gauche}px`
  menu.style.width = `${largeur}px`
  if (dessous >= 200 || dessous >= dessus) {
    menu.style.top = `${r.bottom + 4 - origine}px`
    menu.style.maxHeight = `${Math.max(96, Math.min(320, dessous - 12))}px`
  } else {
    // Au-dessus : son bas contre la zone, quelle que soit sa hauteur.
    menu.style.top = `${r.top - 4 - origine}px`
    menu.style.transform = 'translateY(-100%)'
    menu.style.maxHeight = `${Math.max(96, Math.min(320, dessus - 12))}px`
  }
}

function MenuMentions({
  ancre,
  options,
  actif,
  message,
  onChoisir,
}: {
  ancre: HTMLElement
  options: readonly Option[]
  actif: number
  message: string | null
  onChoisir: (o: Option) => void
}): JSX.Element {
  const menu = useRef<HTMLDivElement>(null)

  // Replacé à chaque rendu (la liste change de hauteur) et à chaque mouvement :
  // défilement, clavier qui s'ouvre, partie visible qui glisse (iPhone).
  useLayoutEffect(() => {
    if (menu.current) placerMenu(menu.current, ancre)
  })
  useEffect(() => {
    const placer = (): void => {
      if (menu.current) placerMenu(menu.current, ancre)
    }
    const vue = window.visualViewport
    window.addEventListener('scroll', placer, true)
    window.addEventListener('resize', placer)
    vue?.addEventListener('resize', placer)
    vue?.addEventListener('scroll', placer)
    return () => {
      window.removeEventListener('scroll', placer, true)
      window.removeEventListener('resize', placer)
      vue?.removeEventListener('resize', placer)
      vue?.removeEventListener('scroll', placer)
    }
  }, [ancre])

  return createPortal(
    // mousedown : le clic dans le menu ne doit pas faire perdre le focus à la zone.
    <div ref={menu} role="listbox" aria-label="Mentionner une personne" onMouseDown={(e) => e.preventDefault()} className="menu-mentions">
      {options.map((o, i) => (
        <div
          key={o.upn}
          role="option"
          aria-selected={i === actif}
          onClick={() => onChoisir(o)}
          className="menu-mentions-option"
          data-actif={i === actif || undefined}
        >
          <PastillePersonne nom={o.nom} upn={o.upn} taille={28} />
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span className="menu-mentions-nom">{o.nom}</span>
            {o.detail && <span className="menu-mentions-detail">{o.detail}</span>}
          </span>
        </div>
      ))}
      {message && <p className="menu-mentions-message">{message}</p>}
    </div>,
    document.body,
  )
}
